import {
  BulkTransactionColumn,
  BulkTransactionRow,
} from '../../bulk-transaction/bulk-transaction.projection'
import {
  collectionRequiredUnsatisfied,
  EditTransactionCollection,
  EditTransactionField,
  requiredUnsatisfied,
} from '../../edit-transaction/edit-transaction.projection'

/** Layout and tallies for the Bulk Transactions grid. */

/**
 * The three gutters hold a glyph and at most a count, so they are sized for that: a glyph, its 3px
 * gap and a three-digit count come to roughly 37px, where 46px left a good part of each empty.
 */
const GUTTER_WIDTH = 40

/** The status gutter: a verdict per row — its glyph, and the count of what the row is short of. */
export const STATUS_GUTTER_WIDTH = GUTTER_WIDTH

/** The changed gutter: the pencil, and how many values the row now holds differently. */
export const CHANGED_GUTTER_WIDTH = GUTTER_WIDTH

/** The row-number gutter. */
export const NUMBER_GUTTER_WIDTH = GUTTER_WIDTH

/** A run of consecutive columns sharing one display group — the band above the header row. */
export interface BulkTransactionBand {
  name: string
  /** How many columns it spans, for `grid-column: span N`. */
  span: number
}

/** Bands, as runs rather than as groups. */
export function bulkBands(
  columns: readonly BulkTransactionColumn[],
): BulkTransactionBand[] {
  const bands: BulkTransactionBand[] = []
  for (const column of columns) {
    const last = bands[bands.length - 1]
    if (last && last.name === column.group) last.span++
    else bands.push({ name: column.group, span: 1 })
  }
  return bands
}

/** A row's standing, which is what the gutter's glyph and its tooltip report. */
export type BulkRowState = 'missing' | 'changed' | 'clean'

export interface BulkRowVerdict {
  state: BulkRowState
  icon: string
  /** Class carrying the tone, so the palette stays in the stylesheet. */
  tone: string
  label: string
  /** Required values this row is still short of. */
  missing: number
  /** Cells holding something other than what they opened with. */
  changed: number
}

/** What one row is short of, and how much of it has been touched. */
export interface BulkRowCounts {
  missing: number
  changed: number
}

/**
 * Count one row. The only thing here that has to project a transaction.
 *
 * Split from {@link bulkRowVerdict} so the grid can hold COUNTS rather than rows: the opening counts
 * come from a scan that projects nothing, and after an edit only the edited row is re-counted.
 * Walking every row to render a glyph is what made the chrome O(selection) per keystroke.
 */
export function bulkRowCounts(row: BulkTransactionRow): BulkRowCounts {
  const fields = Object.values(row.fields)
  // A collection's summary cell is DERIVED from its lines and cannot be typed into, so when it moves
  // the lines have already been counted — counting it too would report one payment as two changes.
  const summaries = new Set(row.collections.map(collection => collection.name))
  return {
    missing:
      fields.filter(requiredUnsatisfied).length +
      row.collections.reduce(
        (total, collection) =>
          total + collectionRequiredUnsatisfied(collection),
        0,
      ),
    changed:
      fields.filter(
        field =>
          !summaries.has(field.field) &&
          field.value !== row.original[field.field],
      ).length +
      row.collections.reduce(
        (total, collection) =>
          total +
          changedLineValues(collection, row.originalLines[collection.name]),
        0,
      ),
  }
}

/**
 * Line values holding something other than what they opened with.
 *
 * Counted so a row edited only through its Item columns still reads as edited: the gutter's pencil
 * is a statement about the transaction, and a payment typed into it is as much a change as a
 * quantity is. A line the row did not open with counts every value it now holds.
 */
const changedLineValues = (
  collection: EditTransactionCollection,
  opening: readonly string[][] = [],
): number => {
  let changed = 0
  collection.rows.forEach((line, index) => {
    line.forEach((value, slot) => {
      if (value !== (opening[index]?.[slot] ?? '')) changed++
    })
  })
  return changed
}

/**
 * How one row reads in the gutter, from its counts alone.
 *
 * Precedence, not a sum: a row that is both edited and incomplete reads as incomplete, because that
 * is the state stopping Save.
 */
export function bulkRowVerdict(
  missing: number,
  changed: number,
): BulkRowVerdict {
  if (missing) {
    return {
      state: 'missing',
      icon: 'mdi-alert-circle',
      tone: 'bt-tone-error',
      label: `${missing} required value${missing === 1 ? '' : 's'} missing`,
      missing,
      changed,
    }
  }
  if (changed) {
    return {
      state: 'changed',
      icon: 'mdi-pencil',
      tone: 'bt-tone-primary',
      label: `${changed} unsaved change${changed === 1 ? '' : 's'}`,
      missing,
      changed,
    }
  }
  return {
    state: 'clean',
    icon: 'mdi-check-circle',
    tone: 'bt-tone-quiet',
    label: 'No changes',
    missing,
    changed,
  }
}

/**
 * One gutter column's reading of a row: its glyph, its tone, its tooltip, and the number beside it.
 *
 * The two marks are INDEPENDENT, which is why they are two columns rather than one verdict with a
 * precedence rule. A row that is both short of values and edited has something to say in both, and
 * {@link bulkRowVerdict}'s "incomplete outranks edited" would have hidden the second.
 */
export interface BulkGutterMark {
  icon: string
  tone: string
  label: string
  /** Shown beside the glyph; null where the mark is the whole statement. */
  count: number | null
}

/**
 * The validity column.
 *
 * A complete row carries the tick and NO number: "ready" is not a quantity, and a 0 beside it would
 * read as a count of something.
 */
export function bulkValidityMark(missing: number): BulkGutterMark {
  return missing
    ? {
        icon: 'mdi-alert-circle',
        tone: 'bt-tone-error',
        label: `${missing} required value${missing === 1 ? '' : 's'} missing`,
        count: missing,
      }
    : {
        icon: 'mdi-check-circle',
        tone: 'bt-tone-valid',
        label: 'Nothing missing',
        count: null,
      }
}

/** The changed column, or nothing at all on a row holding exactly what it opened with. */
export function bulkChangedMark(changed: number): BulkGutterMark | null {
  return changed
    ? {
        icon: 'mdi-pencil',
        tone: 'bt-tone-primary',
        label: `${changed} value${changed === 1 ? '' : 's'} changed`,
        count: changed,
      }
    : null
}

/** One chip of the summary bar. */
export interface BulkTally {
  label: string
  icon: string
  tone: string
}

/**
 * The three readings of the grid's state, as ROWS rather than values.
 *
 * "1 Invalid" is one transaction that cannot be posted, however many values it is short of — the
 * gutter is where a row says how many. A tone is only spent on a non-zero count, so the bar's
 * colour says what the grid needs at a glance rather than lighting up regardless.
 */
export function bulkTallies(
  rowCount: number,
  rowsInvalid: number,
  rowsChanged: number,
): BulkTally[] {
  return [
    {
      label: `${rowCount - rowsInvalid} Valid`,
      icon: 'mdi-check-circle',
      tone: rowCount - rowsInvalid ? 'bt-tone-valid' : 'bt-tone-quiet',
    },
    {
      label: `${rowsInvalid} Invalid`,
      icon: 'mdi-alert-circle',
      tone: rowsInvalid ? 'bt-tone-error' : 'bt-tone-quiet',
    },
    {
      label: `${rowsChanged} Changed`,
      icon: 'mdi-pencil',
      tone: rowsChanged ? 'bt-tone-primary' : 'bt-tone-quiet',
    },
  ]
}

/**
 * The note beneath the grid when Save is refused.
 *
 * The footer is ONE input for the whole grid, so a missing footer value is one missing value and not
 * one per row — saying "2 required values missing" for a single empty Notes box would send the user
 * hunting through the rows for a second one.
 */
export function bulkMissingLabel(
  inRows: number,
  rowsShort: number,
  footerMissing: number,
): string {
  if (footerMissing && !inRows) {
    return `${footerMissing} required footer value${
      footerMissing === 1 ? '' : 's'
    } missing`
  }
  const rows = `${inRows} required value${
    inRows === 1 ? '' : 's'
  } missing in ${rowsShort} transaction${rowsShort === 1 ? '' : 's'}`
  return footerMissing ? `${rows}, and ${footerMissing} in the footer` : rows
}

/** Required footer fields still holding nothing. */
export function bulkFooterMissing(
  footer: readonly EditTransactionField[],
): number {
  return footer.filter(requiredUnsatisfied).length
}
